import { memo, useEffect } from 'react';
import { Bot, Mail, AlertTriangle, Ban, RefreshCw, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DeliveryBreakdown } from '../types';

interface TabDeliveryProps {
  breakdown: DeliveryBreakdown | null;
  loading: boolean;
  error: string | null;
  onLoad: () => void;
  isPending: boolean;
}

const KIND_META: Record<
  DeliveryBreakdown['groups'][number]['kind'],
  { icon: typeof Bot; tone: string; label: (username: string | null) => string; hint?: string }
> = {
  TELEGRAM_BOT: {
    icon: Bot,
    tone: 'text-sky-600 dark:text-sky-400',
    label: (username) => (username ? `@${username}` : 'Telegram-бот'),
    hint: 'Получат через бота, которого запускали',
  },
  FALLBACK: {
    icon: AlertTriangle,
    tone: 'text-yellow-600 dark:text-yellow-400',
    label: () => 'Без привязки к боту',
    hint: 'Нет активной связи — уйдёт первым активным ботом, возможен отказ (403)',
  },
  EMAIL: {
    icon: Mail,
    tone: 'text-violet-600 dark:text-violet-400',
    label: () => 'Электронная почта',
    hint: 'Получат письмо на email',
  },
  SKIPPED: {
    icon: Ban,
    tone: 'text-muted-foreground',
    label: () => 'Не получат',
    hint: 'Нет ни Telegram, ни email — ни один канал не доставит',
  },
};

export const TabDelivery = memo(function TabDelivery({
  breakdown,
  loading,
  error,
  onLoad,
  isPending,
}: TabDeliveryProps) {
  // Подгружаем разбивку при первом открытии вкладки.
  useEffect(() => {
    if (!breakdown && !loading && !error) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Кто и как получит рассылку</h3>
          <p className="text-sm text-muted-foreground">
            Разбивка текущей аудитории по фактическому каналу и боту доставки
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onLoad} disabled={loading || isPending}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {!error && breakdown && (
        <>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Всего адресатов доставки:{' '}
            <span className="font-semibold text-foreground">
              {breakdown.total.toLocaleString('ru-RU')}
            </span>
          </div>

          {breakdown.groups.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              По текущим условиям нет получателей
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {breakdown.groups.map((group, idx) => {
                const meta = KIND_META[group.kind];
                const Icon = meta.icon;
                return (
                  <div
                    key={`${group.kind}-${group.botId ?? idx}`}
                    className="flex items-start gap-3 rounded-lg border p-4"
                  >
                    <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${meta.tone}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium truncate">{meta.label(group.botUsername)}</span>
                        <span className="font-bold tabular-nums">
                          {group.count.toLocaleString('ru-RU')}
                        </span>
                      </div>
                      {meta.hint && (
                        <p className="text-xs text-muted-foreground mt-1">{meta.hint}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {breakdown.groups.some((g) => g.kind === 'FALLBACK') && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  У части пользователей нет активной связи с ботом. Им сообщение уйдёт через
                  первого активного бота, и если они его не запускали — Telegram вернёт ошибку,
                  доставка не состоится.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});
